import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import {
    getSupplier,
    getSupplierCollectionsContact,
    getSupplierInvoices,
    getSupplierNotes,
    getUsers,
    syncSupplierAddresses,
    updateSupplierPhoneNumbers,
    toastSuccess,
} from '../../api'
import { SupplierAddressesCard } from './addresses'
import { SupplierPhoneNumbersCard } from './phoneNumbers'
import { SupplierInvoicesCard } from './supplierCollections'
import { InsufficientPermissions, Loading, ScrollableCol, ScrollableContainer } from '../common'
import { SupplierCard } from './card'
import { SupplierMergeCard } from './supplierMerge'
import {
    Invoice,
    JsonSupplierCollectionsContact,
    JsonSupplierNote,
    Supplier,
    SupplierCollectionsContact,
    SupplierNote,
    User,
} from '../../models'
import { SupplierNotes } from './notes'
import { getAuthUser } from '../../helpers'
import defineAbility from './defineAbility'
import { ScheduleCallbackForm } from './ScheduleCallbackForm'
import { RecordingManager } from './recordingManager'
import { SupplierCreateInvoiceCard } from './createInvoiceCard'
import { SupplierEmailInvoiceCard } from './emailInvoiceCard'

export const SupplierComponent = () => {
    const [supplier, setSupplier] = useState<Supplier | undefined>()
    const [supplierNotes, setSupplierNotes] = useState<SupplierNote[]>([])
    const [supplierCollectionsContacts, setSupplierCollectionsContacts] = useState<SupplierCollectionsContact[]>([])
    const [invoices, setInvoices] = useState<Invoice[]>([])
    const [isCollection, setIsCollection] = useState<boolean>(false)
    const [triggerNotesRefresh, setTriggerNotesRefresh] = useState(false)
    const [users, setUsers] = useState<User[]>([])
    const [triggerRefresh, setTriggerRefresh] = useState(false)
    const [hasAccess, setHasAccess] = useState<boolean | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    const user = getAuthUser()
    const ability = defineAbility(user)

    const params = useParams()
    const navigate = useNavigate()

    useEffect(() => {
        getUsers()
            .then(response => {
                setUsers(response.data)
            })
            .catch(error => {
                if (error.response && error.response.status === 403) {
                    setUsers([])
                } else {
                    console.error('Error fetching users:', error)
                }
            })
    }, [])

    useEffect(() => {
        let cancelled = false

        async function loadSupplierPage() {
            if (!params.supplierId) return

            setIsLoading(true)
            setHasAccess(null)

            const supplierId = params.supplierId

            try {
                const supplierResponse = await getSupplier(supplierId)

                if (cancelled) return

                const supplierData = new Supplier(supplierResponse.data)
                setSupplier(supplierData)
                setIsCollection(supplierData.isCollections)
                setHasAccess(true)

                const [
                    notesResponse,
                    contactsResponse,
                    invoicesResponse,
                    addressesResponse
                ] = await Promise.all([
                    getSupplierNotes(supplierId),
                    getSupplierCollectionsContact(supplierId),
                    getSupplierInvoices(supplierId),
                    syncSupplierAddresses(supplierId, params)
                ])

                if (cancelled) return

                setSupplierNotes(
                    notesResponse.data.map(
                        (note: JsonSupplierNote) => new SupplierNote(note)
                    )
                )

                setSupplierCollectionsContacts(
                    contactsResponse.data.map(
                        (contact: JsonSupplierCollectionsContact) =>
                            new SupplierCollectionsContact(contact)
                    )
                )

                setInvoices(invoicesResponse.data)
                setSupplier(new Supplier(addressesResponse.data))
            } catch (error: any) {
                if (cancelled) return

                if (error?.response?.status === 403) {
                    setHasAccess(false)
                    setSupplier(undefined)
                    return
                }
            } finally {
                if (!cancelled) setIsLoading(false)
            }
        }

        loadSupplierPage()

        return () => {
            cancelled = true
        }
    }, [params.supplierId, triggerNotesRefresh, triggerRefresh])

    if (hasAccess === false) {
        return <InsufficientPermissions />
    }

    if (isLoading || !(supplier instanceof Supplier)) {
        return <Loading />
    }

    const toggleNotesRefresh = () => setTriggerNotesRefresh(prevState => !prevState)
    const handleMergeSuccess = () => setTriggerRefresh(prevState => !prevState)

    const onPhoneNumbersChanged = (
        phoneNumbers: string[],
        primaryPhoneNumber: string,
        successCallback?: () => void
    ) => {
        const params = {
            phoneNumbers,
            primaryPhoneNumber
        }
        updateSupplierPhoneNumbers(supplier.id.toString(), params)
            .then((response) => {
                setSupplier(new Supplier(response.data))

                if (successCallback) {
                    successCallback()
                }

                toastSuccess('Supplier Phone Numbers Updated')

            })
            .catch((error) => {
                const { response } = error

                if (response.status === 409) {
                    const { conflicts } = response.data

                    interface SupplierPhoneConflict {
                        phone: string
                        supplier_id: number
                    }

                    toast.error((
                        <div>
                            Supplier Phone Number(s) exist:
                            {conflicts.map((conflict: SupplierPhoneConflict) => {
                                const SupplierLink = () => (
                                    <Link to={`/suppliers/${conflict.supplier_id}`}>
                                        {conflict.supplier_id}
                                    </Link>
                                )

                                return (
                                    <p>
                                        {conflict.phone} - Supplier <SupplierLink />
                                    </p>
                                )
                            })}
                        </div>
                    ), { autoClose: false })
                }
            })
    }

    const onSupplierContactsChanged = (newContacts: SupplierCollectionsContact[]) => {
        setSupplierCollectionsContacts(newContacts)
    }

    return (
        <div className='container'>
            <ScrollableContainer className='row'>
                <ScrollableCol className='col-xs-12 col-md-6'>
                    <SupplierCard
                        setSupplier={setSupplier}
                        supplier={supplier}
                        onNoteChange={toggleNotesRefresh} />
                    {ability.can('View', 'SupplierCallbacks') && (
                        <ScheduleCallbackForm
                            supplierId={supplier.id}
                            supplierName={supplier.name}
                            users={users}
                        />
                    )}
                    <SupplierNotes
                        supplierId={supplier.id}
                        supplierNotes={supplierNotes} />
                </ScrollableCol>
                <ScrollableCol className='col-xs-12 col-md-6'>
                    {ability.can('Create', 'SupplierInvoice') &&
                        <SupplierCreateInvoiceCard
                            supplier={supplier}
                            onSuccess={(response) => setTimeout(
                                () => navigate(`/invoices/${response.data.invoice.id}`)
                            )}
                            userId={user.id} />
                    }
                    <SupplierAddressesCard
                        addresses={supplier.addresses}
                        supplierId={supplier.id}
                        onAddressesChanged={() => setTriggerRefresh(prev => !prev)} />
                    <SupplierPhoneNumbersCard
                        key={supplier.id}
                        onPhoneNumbersChanged={onPhoneNumbersChanged}
                        phoneNumbers={supplier.phoneNumbers}
                        primaryPhoneNumber={supplier.primaryPhone} />
                    <SupplierEmailInvoiceCard
                        invoices={invoices}
                        supplierContacts={supplierCollectionsContacts}
                        supplierId={supplier.id}
                        onSupplierContactsChanged={onSupplierContactsChanged}
                    />
                    {ability.can('View', 'SupplierCollections') && isCollection ?
                        <SupplierInvoicesCard
                            invoices={invoices}
                            supplierContacts={supplierCollectionsContacts}
                            supplierId={supplier.id}
                            onSupplierContactsChanged={onSupplierContactsChanged}
                        />
                        : null
                    }
                    <RecordingManager supplierId={supplier.id} users={users} />
                    <SupplierMergeCard
                        supplierId={supplier.id}
                        onMergeSuccess={handleMergeSuccess} />
                </ScrollableCol>
            </ScrollableContainer>
        </div>
    )
}
